
import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { Calculator } from "lucide-react";

export default function DCACalculator({ recommendation }) {
    // Defensive check
    if (!recommendation) {
        return <div className="text-slate-500 text-sm p-4">Recommendation data needed for calculation.</div>;
    }

    const [monthlyAmount, setMonthlyAmount] = useState(1000000);
    const [duration, setDuration] = useState(12);

    const currentPrice = recommendation.current_price || recommendation.entry_price || 0;
    const targetPrice = recommendation.target_price || (currentPrice * 1.1) || 0;

    const totalInvestment = monthlyAmount * duration;
    // Prevent division by zero
    const sharesPerMonth = currentPrice > 0 ? Math.floor(monthlyAmount / currentPrice) : 0;
    const totalShares = sharesPerMonth * duration;
    const avgPrice = totalShares > 0 ? totalInvestment / totalShares : 0;
    const projectedValue = totalShares * targetPrice;
    const projectedProfit = projectedValue - totalInvestment;
    const projectedReturnPercent = totalInvestment > 0 ? ((projectedValue - totalInvestment) / totalInvestment) * 100 : 0;

    return (
        <Card className="glass-effect border-slate-700">
            <CardContent className="p-4 md:p-6">
                <div className="space-y-6">
                    {/* Input Controls */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-white text-sm font-medium mb-2 block">
                                Pelaburan Bulanan
                            </label>
                            <Input
                                type="number"
                                value={monthlyAmount}
                                onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                                placeholder="1000"
                            />
                        </div>

                        <div>
                            <label className="text-slate-300 text-sm font-medium mb-2 block">
                                Tempoh: {duration} Bulan
                            </label>
                            <Slider
                                value={[duration]}
                                onValueChange={(value) => setDuration(value[0])}
                                max={60}
                                min={3}
                                step={1}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Calculation Results */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        <div className="text-center p-3 rounded-lg bg-blue-500/10">
                            <div className="text-slate-400 text-xs mb-1">Jumlah Pelaburan</div>
                            <div className="text-sm md:text-base font-bold text-blue-400">
                                RM {totalInvestment.toLocaleString('en-MY')}
                            </div>
                        </div>

                        <div className="text-center p-3 rounded-lg bg-purple-500/10">
                            <div className="text-slate-400 text-xs mb-1">Jumlah Unit</div>
                            <div className="text-sm md:text-base font-bold text-purple-400">
                                {totalShares.toLocaleString('en-MY')} unit
                            </div>
                        </div>

                        <div className="text-center p-3 rounded-lg bg-emerald-500/10">
                            <div className="text-slate-400 text-xs mb-1">Anggaran Nilai</div>
                            <div className="text-sm md:text-base font-bold text-emerald-400">
                                RM {projectedValue.toLocaleString('en-MY', { maximumFractionDigits: 2 })}
                            </div>
                        </div>

                        <div className="text-center p-3 rounded-lg bg-green-500/10">
                            <div className="text-slate-400 text-xs mb-1">Anggaran Untung</div>
                            <div className="text-sm md:text-base font-bold text-green-400">
                                +{projectedReturnPercent.toFixed(1)}%
                            </div>
                            <div className="text-xs text-slate-500">
                                RM {projectedProfit.toLocaleString('en-MY', { maximumFractionDigits: 2 })}
                            </div>
                        </div>
                    </div>

                    {/* DCA Strategy Explanation */}
                    <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
                        <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                            <Calculator className="w-4 h-4" />
                            Strategi Dollar Cost Averaging (DCA)
                        </h4>
                        <div className="text-sm text-slate-300 space-y-2">
                            <p>• Labur secara konsisten sebanyak {sharesPerMonth} unit setiap bulan selama {duration} bulan</p>
                            <p>• Purata harga: RM {avgPrice.toLocaleString('en-MY', { maximumFractionDigits: 2 })}</p>
                            <p>• Mengurangkan risiko volatiliti pasaran dengan pembelian berkala</p>
                            <p>• Sesuai untuk pelaburan jangka panjang bagi saham fundamental kukuh</p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
